/**
 * CPU-side port of the v3 ocean vertex shader's wave math.
 * Must match SphericalOcean.tsx's Gerstner + domain warp + FBM exactly.
 *
 * Used by ShipController and DockedShips for buoyancy probes.
 */

import { getIslands, RADIUS } from '@/world/terrain'
import {
  WAVE_DIRS,
  DEFAULT_AMPLITUDE,
  DEFAULT_WAVELENGTH,
  DEFAULT_SPEED,
  DEFAULT_GERSTNER_LAYERS,
  DEFAULT_NOISE_OCTAVES,
  DEFAULT_NOISE_STRENGTH,
} from '@/world/ocean/SphericalOcean'

// Island attenuation falloff — must match SphericalOcean component
const FALLOFF = 3000.0
const MIN_SCALE = 0.04

// ============================================================================
// Simplex 3D noise — matches GPU snoise() exactly (Ashima)
// ============================================================================

function snoiseCPU(x: number, y: number, z: number): number {
  const F3 = 1 / 3, G3 = 1 / 6
  const s = (x + y + z) * F3
  const i = Math.floor(x + s), j = Math.floor(y + s), k = Math.floor(z + s)
  const t = (i + j + k) * G3
  const X0 = i - t, Y0 = j - t, Z0 = k - t
  const x0 = x - X0, y0 = y - Y0, z0 = z - Z0

  let i1: number, j1: number, k1: number, i2: number, j2: number, k2: number
  if (x0 >= y0) {
    if (y0 >= z0) { i1 = 1; j1 = 0; k1 = 0; i2 = 1; j2 = 1; k2 = 0 }
    else if (x0 >= z0) { i1 = 1; j1 = 0; k1 = 0; i2 = 1; j2 = 0; k2 = 1 }
    else { i1 = 0; j1 = 0; k1 = 1; i2 = 1; j2 = 0; k2 = 1 }
  } else {
    if (y0 < z0) { i1 = 0; j1 = 0; k1 = 1; i2 = 0; j2 = 1; k2 = 1 }
    else if (x0 < z0) { i1 = 0; j1 = 1; k1 = 0; i2 = 0; j2 = 1; k2 = 1 }
    else { i1 = 0; j1 = 1; k1 = 0; i2 = 1; j2 = 1; k2 = 0 }
  }

  const x1 = x0 - i1 + G3, y1 = y0 - j1 + G3, z1 = z0 - k1 + G3
  const x2 = x0 - i2 + 2 * G3, y2 = y0 - j2 + 2 * G3, z2 = z0 - k2 + 2 * G3
  const x3 = x0 - 1 + 3 * G3, y3 = y0 - 1 + 3 * G3, z3 = z0 - 1 + 3 * G3

  function m289(v: number) { return v - Math.floor(v / 289) * 289 }
  function pm(v: number) { return m289((v * 34 + 1) * v) }

  const grad3 = [
    [1, 1, 0], [-1, 1, 0], [1, -1, 0], [-1, -1, 0],
    [1, 0, 1], [-1, 0, 1], [1, 0, -1], [-1, 0, -1],
    [0, 1, 1], [0, -1, 1], [0, 1, -1], [0, -1, -1],
  ]

  function ghash(ii: number, jj: number, kk: number) {
    return Math.abs(pm(ii + pm(jj + pm(kk)))) % 12
  }

  function dot3(g: number[], a: number, b: number, c: number) {
    return g[0] * a + g[1] * b + g[2] * c
  }

  const ii = m289(i), jj = m289(j), kk = m289(k)
  const gi0 = ghash(ii, jj, kk)
  const gi1 = ghash(ii + i1, jj + j1, kk + k1)
  const gi2 = ghash(ii + i2, jj + j2, kk + k2)
  const gi3 = ghash(ii + 1, jj + 1, kk + 1)

  let t0 = 0.6 - x0 * x0 - y0 * y0 - z0 * z0
  const n0 = t0 < 0 ? 0 : (t0 *= t0, t0 * t0 * dot3(grad3[gi0], x0, y0, z0))
  let t1 = 0.6 - x1 * x1 - y1 * y1 - z1 * z1
  const n1 = t1 < 0 ? 0 : (t1 *= t1, t1 * t1 * dot3(grad3[gi1], x1, y1, z1))
  let t2 = 0.6 - x2 * x2 - y2 * y2 - z2 * z2
  const n2 = t2 < 0 ? 0 : (t2 *= t2, t2 * t2 * dot3(grad3[gi2], x2, y2, z2))
  let t3 = 0.6 - x3 * x3 - y3 * y3 - z3 * z3
  const n3 = t3 < 0 ? 0 : (t3 *= t3, t3 * t3 * dot3(grad3[gi3], x3, y3, z3))

  return 32 * (n0 + n1 + n2 + n3)
}

// ============================================================================
// FBM — matches GPU fbm() exactly
// ============================================================================

function fbmCPU(x: number, y: number, z: number, oct: number): number {
  let val = 0, amp = 0.5, freq = 1, px = x, py = y, pz = z
  for (let i = 0; i < oct; i++) {
    val += amp * snoiseCPU(px * freq, py * freq, pz * freq)
    freq *= 2.17; amp *= 0.48
    px += 1.73; py += 9.21; pz += 4.37
  }
  return val
}

// ============================================================================
// Rotation helpers — match GPU rotX/rotY/rotZ
// ============================================================================

function rotateYX(x: number, y: number, z: number, ay: number, ax: number): [number, number, number] {
  const cy = Math.cos(ay), sy = Math.sin(ay)
  const rx = cy * x + sy * z, ry = y, rz = -sy * x + cy * z
  const cx = Math.cos(ax), sx = Math.sin(ax)
  return [rx, cx * ry - sx * rz, sx * ry + cx * rz]
}

function rotateZY(x: number, y: number, z: number, az: number, ay: number): [number, number, number] {
  const cz = Math.cos(az), sz = Math.sin(az)
  const rx = cz * x - sz * y, ry = sz * x + cz * y, rz = z
  const cy = Math.cos(ay), sy = Math.sin(ay)
  return [cy * rx + sy * rz, ry, -sy * rx + cy * rz]
}

// ============================================================================
// Island attenuation — matches GPU getAtten()
// ============================================================================

function getAtten(nx: number, ny: number, nz: number): number {
  let mn = 1e9
  const islands = getIslands()
  for (const isl of islands) {
    const dot = nx * isl.nx + ny * isl.ny + nz * isl.nz
    const arc = Math.acos(Math.max(-1, Math.min(1, dot))) * RADIUS
    if (arc < mn) mn = arc
  }
  let t = Math.max(0, Math.min(1, mn / FALLOFF))
  t = t * t * (3 - 2 * t)
  return MIN_SCALE + (1 - MIN_SCALE) * t
}

// ============================================================================
// Domain warp — matches GPU domainWarp()
// ============================================================================

function domainWarpCPU(sx: number, sy: number, sz: number, time: number): [number, number, number] {
  const n1x = snoiseCPU(sx * 1.8 + 0.0, sy * 1.8 + 0.0, sz * 1.8 + time * 0.02)
  const n1y = snoiseCPU(sx * 1.8 + 5.7, sy * 1.8 + 2.3, sz * 1.8 + time * 0.02)
  const n1z = snoiseCPU(sx * 1.8 + 1.4, sy * 1.8 + 8.1, sz * 1.8 + time * 0.02)

  const n2x = snoiseCPU(sx * 3.6 + n1x * 0.5 + time * 0.015, sy * 3.6 + n1y * 0.5 + 0.0, sz * 3.6 + n1z * 0.5 + 0.0)
  const n2y = snoiseCPU(sx * 3.6 + n1x * 0.5 + 3.1, sy * 3.6 + n1y * 0.5 + 7.2, sz * 3.6 + n1z * 0.5 + time * 0.015)
  const n2z = snoiseCPU(sx * 3.6 + n1x * 0.5 + 6.8, sy * 3.6 + n1y * 0.5 + 1.5, sz * 3.6 + n1z * 0.5 + time * 0.015)

  return [
    sx + n1x * 0.12 + n2x * 0.06,
    sy + n1y * 0.12 + n2y * 0.06,
    sz + n1z * 0.12 + n2z * 0.06,
  ]
}

// ============================================================================
// Main export: sample ocean height at a unit normal on the sphere
// Returns the radial displacement (Gerstner height + noise) from base radius
// ============================================================================

export function sampleOceanHeight(nx: number, ny: number, nz: number, time: number): number {
  const atten = getAtten(nx, ny, nz)
  const spd = time * DEFAULT_SPEED
  const baseFreq = 2 * Math.PI / DEFAULT_WAVELENGTH

  const ws = domainWarpCPU(nx, ny, nz, spd)
  const wsx = ws[0], wsy = ws[1], wsz = ws[2]

  // Gerstner waves — only compute radial (height) component for buoyancy
  let totalH = 0
  for (let i = 0; i < DEFAULT_GERSTNER_LAYERS; i++) {
    const amp = DEFAULT_AMPLITUDE * Math.pow(0.62, i) * atten
    const freq = baseFreq * Math.pow(1.68, i)
    const phase = -spd * Math.sqrt(9.81 * freq) + i * 1.374

    const dir = WAVE_DIRS[i]
    const d = (wsx * dir.x + wsy * dir.y + wsz * dir.z) * RADIUS
    totalH += amp * Math.sin(d * freq + phase)
  }

  // Noise displacement
  let noiseH = 0
  if (DEFAULT_NOISE_OCTAVES > 0) {
    const r1 = rotateYX(nx * 3.5, ny * 3.5, nz * 3.5, spd * 0.042, spd * 0.03)
    noiseH = fbmCPU(r1[0], r1[1], r1[2], DEFAULT_NOISE_OCTAVES) * DEFAULT_NOISE_STRENGTH * atten

    const r2 = rotateZY(nx * 12, ny * 12, nz * 12, spd * 0.072, spd * 0.048)
    noiseH += snoiseCPU(r2[0], r2[1], r2[2]) * DEFAULT_NOISE_STRENGTH * 0.25 * atten
  }

  return totalH + noiseH
}
